import { Plus, RefreshCw, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { formatCurrency, formatUtcDateOnly } from "@/lib/format";
import { isUsdCurrency } from "../bill-shared";
import type { BillExtractionInfo, SuggestionConfidence } from "./page";

/**
 * The AI extraction panel: it turns a document reading into a set of one-tap
 * field suggestions. The three states are deliberately distinct — processing
 * shows placeholder rows in the shape of the answer that is coming (so the
 * panel does not jump when it lands), failure is recoverable in place, and a
 * successful reading foregrounds "Use all" as the primary move.
 */

const CONFIDENCE_STYLE: Record<
  SuggestionConfidence,
  { dot: string; text: string; label: string }
> = {
  high: {
    dot: "bg-emerald-500",
    text: "text-emerald-700 dark:text-emerald-400",
    label: "High confidence",
  },
  medium: {
    dot: "bg-amber-500",
    text: "text-amber-700 dark:text-amber-400",
    label: "Medium confidence",
  },
  low: {
    dot: "bg-destructive",
    text: "text-destructive",
    label: "Low confidence",
  },
};

function ConfidencePill({ confidence }: { confidence: SuggestionConfidence }) {
  const style = CONFIDENCE_STYLE[confidence];
  return (
    <span
      className={cn("flex items-center gap-1 text-[10px] font-medium", style.text)}
      title={style.label}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full", style.dot)} />
      {confidence}
    </span>
  );
}

function SuggestionRow({
  label,
  display,
  confidence,
  evidence,
  onApply,
}: {
  label: string;
  display: string;
  confidence: SuggestionConfidence;
  evidence?: string;
  onApply?: () => void;
}) {
  return (
    <div
      className="group flex items-center gap-2 rounded px-1 py-0.5 text-xs hover:bg-primary/5"
      title={evidence ? `Evidence: ${evidence}` : undefined}
    >
      <span className="text-muted-foreground w-24 shrink-0">{label}</span>
      <span className="min-w-0 flex-1 truncate font-medium">{display}</span>
      <ConfidencePill confidence={confidence} />
      {onApply && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-primary h-5 px-1.5 text-[10px] opacity-60 group-hover:opacity-100"
          onClick={onApply}
        >
          Use
        </Button>
      )}
    </div>
  );
}

/** Placeholder rows in the shape of the real suggestions, shown while reading. */
function ProcessingRows() {
  const widths = ["w-40", "w-24", "w-28", "w-28", "w-20"];
  return (
    <div className="space-y-1" aria-hidden>
      {widths.map((width, index) => (
        <div key={index} className="flex items-center gap-2 px-1 py-0.5">
          <Skeleton className="h-3.5 w-24 shrink-0" />
          <Skeleton className={cn("h-3.5", width)} />
          <Skeleton className="ml-auto h-3.5 w-10" />
        </div>
      ))}
    </div>
  );
}

export function BillAiPanel({
  extraction,
  suggestions,
  vendorMatched,
  onUseAll,
  onClearAutoApplied,
  onRerun,
  vendorLookupPending = false,
  onCreateVendor,
  onApplyVendor,
  onApplyInvoiceNumber,
  onApplyInvoiceDate,
  onApplyDueDate,
  onApplyTotal,
}: {
  extraction: BillExtractionInfo;
  suggestions: BillExtractionInfo["suggestions"] | undefined;
  vendorMatched: boolean;
  onUseAll: () => void;
  onClearAutoApplied?: () => void;
  onRerun?: () => void;
  vendorLookupPending?: boolean;
  onCreateVendor?: () => void;
  onApplyVendor: () => void;
  onApplyInvoiceNumber: () => void;
  onApplyInvoiceDate: () => void;
  onApplyDueDate: () => void;
  onApplyTotal: () => void;
}) {
  const processing = extraction.status === "processing";
  const nothingRead =
    suggestions &&
    !suggestions.vendorNameRaw &&
    !suggestions.invoiceNumber &&
    !suggestions.invoiceDate &&
    !suggestions.dueDate &&
    !suggestions.totalAmount;

  return (
    <div
      className={cn(
        "space-y-2 rounded-md border p-2.5 transition-colors",
        processing
          ? "border-primary/30 bg-primary/[0.05]"
          : "border-primary/25 bg-primary/[0.04]",
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-xs font-semibold">
          <Sparkles
            className={cn(
              "h-3.5 w-3.5 text-primary",
              processing && "animate-pulse",
            )}
          />
          AI suggestions
          <span className="text-muted-foreground font-normal">
            {processing ? "reading…" : `attempt ${extraction.attempt}`}
          </span>
        </span>
        <span className="flex items-center gap-1.5">
          {suggestions && !nothingRead && (
            <Button
              type="button"
              size="sm"
              className="h-7 px-2.5 text-xs"
              onClick={onUseAll}
            >
              <Sparkles className="mr-1 h-3 w-3" />
              Use all
            </Button>
          )}
          {!processing && onRerun && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={onRerun}
            >
              <RefreshCw className="mr-1 h-3 w-3" />
              Re-run
            </Button>
          )}
        </span>
      </div>

      {processing && (
        <>
          <p className="text-muted-foreground text-xs">
            Reading the invoice and drafting field suggestions. This usually
            takes a few seconds.
          </p>
          <ProcessingRows />
        </>
      )}

      {(extraction.autoAppliedFields?.length ?? 0) > 0 && (
        <div className="flex items-center justify-between gap-2 rounded bg-primary/[0.06] px-2 py-1">
          <span className="text-muted-foreground text-xs">
            {extraction.autoAppliedFields!.length} field
            {extraction.autoAppliedFields!.length === 1 ? "" : "s"} filled
            automatically from this reading.
          </span>
          {onClearAutoApplied && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-5 shrink-0 px-1.5 text-[10px]"
              onClick={onClearAutoApplied}
            >
              Clear AI-filled
            </Button>
          )}
        </div>
      )}

      {extraction.status === "failed" && (
        <div className="flex items-center justify-between gap-2">
          <p className="text-destructive text-xs">
            Couldn’t read the document: {extraction.error ?? "unknown error"}
          </p>
          {onRerun && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-6 shrink-0 px-2 text-xs"
              onClick={onRerun}
            >
              <RefreshCw className="mr-1 h-3 w-3" />
              Try again
            </Button>
          )}
        </div>
      )}

      {suggestions && (
        <div className="space-y-0.5">
          {suggestions.vendorNameRaw &&
            (vendorLookupPending ? (
              <p className="rounded bg-muted px-2 py-1 text-xs text-muted-foreground">
                Checking the vendor list for “{suggestions.vendorNameRaw.value}”…
              </p>
            ) : vendorMatched ? (
              <SuggestionRow
                label="Vendor"
                display={suggestions.vendorNameRaw.value}
                confidence={suggestions.vendorNameRaw.confidence}
                evidence={suggestions.vendorNameRaw.evidence}
                onApply={onApplyVendor}
              />
            ) : (
              <div className="flex items-center gap-2 rounded bg-amber-500/10 px-2 py-1 text-xs text-amber-700 dark:text-amber-400">
                <p className="min-w-0 flex-1">
                  Vendor “{suggestions.vendorNameRaw.value}” isn’t in the vendor
                  list —{" "}
                  {onCreateVendor
                    ? "pick one manually or create it now."
                    : "pick or manage it in Bills settings."}
                </p>
                {onCreateVendor && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-6 shrink-0 border-amber-600/30 bg-background px-2 text-[10px] text-amber-800 hover:bg-amber-500/10 hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-200"
                    onClick={onCreateVendor}
                  >
                    <Plus className="mr-1 h-3 w-3" />
                    Add vendor
                  </Button>
                )}
              </div>
            ))}
          {suggestions.invoiceNumber && (
            <SuggestionRow
              label="Invoice #"
              display={suggestions.invoiceNumber.value}
              confidence={suggestions.invoiceNumber.confidence}
              evidence={suggestions.invoiceNumber.evidence}
              onApply={onApplyInvoiceNumber}
            />
          )}
          {suggestions.invoiceDate && (
            <SuggestionRow
              label="Invoice date"
              display={formatUtcDateOnly(suggestions.invoiceDate.value, {
                month: "short",
              })}
              confidence={suggestions.invoiceDate.confidence}
              evidence={suggestions.invoiceDate.evidence}
              onApply={onApplyInvoiceDate}
            />
          )}
          {suggestions.dueDate && (
            <SuggestionRow
              label="Due date"
              display={formatUtcDateOnly(suggestions.dueDate.value, {
                month: "short",
              })}
              confidence={suggestions.dueDate.confidence}
              evidence={suggestions.dueDate.evidence}
              onApply={onApplyDueDate}
            />
          )}
          {suggestions.totalAmount && (
            <SuggestionRow
              label="Total"
              display={formatCurrency(suggestions.totalAmount.value)}
              confidence={suggestions.totalAmount.confidence}
              evidence={suggestions.totalAmount.evidence}
              onApply={onApplyTotal}
            />
          )}
          {suggestions.currency && !isUsdCurrency(suggestions.currency.value) && (
            <p className="text-destructive px-1 pt-0.5 text-xs">
              The document appears to be in {suggestions.currency.value} — bills
              must be USD.
            </p>
          )}
          {nothingRead && (
            <p className="text-muted-foreground px-1 text-xs">
              Nothing could be read from the document with confidence. Enter the
              fields manually, or re-run if the scan was poor.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
